import { Component, ErrorInfo, ReactNode } from 'react';

import { Button } from '@progress/kendo-react-buttons';
import { QueryErrorResetBoundary } from '@tanstack/react-query';

import { isApiUnreachableError } from '../api/apiErrors';

interface BoundaryProps {
  onReset: () => void;
  children: ReactNode;
}

interface BoundaryState {
  error: unknown;
}

class ErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('Plan Sponsor Portal failed to render', error, info.componentStack);
  }

  handleRetry = () => {
    this.props.onReset();
    this.setState({ error: null });
  };

  // An error boundary has to return either its children (ReactNode) or the fallback element.
  // eslint-disable-next-line sonarjs/function-return-type
  render(): ReactNode {
    const { error } = this.state;
    return error === null ? (
      this.props.children
    ) : (
      <ApiErrorPage error={error} onRetry={this.handleRetry} />
    );
  }
}

const ApiErrorPage = ({ error, onRetry }: { error: unknown; onRetry: () => void }) => {
  const unreachable = isApiUnreachableError(error);

  return (
    <div className="api-error" role="alert">
      <h2>
        {unreachable ? 'The portal is temporarily unavailable' : 'Something went wrong'}
      </h2>
      <p>
        {unreachable
          ? "We couldn't connect to the SBC Plan Sponsor Portal service. This is usually temporary. Please try again in a few minutes."
          : 'An unexpected error occurred while loading this page. Please try again.'}
      </p>
      <p>If the problem continues, contact your Aetna Account Representative.</p>
      <Button themeColor="primary" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
};

/**
 * Shows a friendly error page instead of a broken/empty screen when a query fails after
 * its retries (see QueryClient defaults in main.tsx). "Try again" resets the failed
 * queries so they refetch when the children remount.
 */
const ApiErrorBoundary = ({ children }: { children: ReactNode }) => (
  <QueryErrorResetBoundary>
    {({ reset }) => <ErrorBoundary onReset={reset}>{children}</ErrorBoundary>}
  </QueryErrorResetBoundary>
);

export default ApiErrorBoundary;
